import { useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { businessRoutes } from '@/lib/route-map';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, ExternalLink, Loader2, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { CHART } from '@/lib/chart-theme';
import { cn } from '@/lib/utils';
import type { SurveyResponseListItem, SurveyResponseView, SurveyStatus } from '@/lib/types';
import type { FlagBreakdown, NumericStat, WtpSummary } from '@pageboss/shared/survey';
import { SURVEY_STATUS_META, countryName, fmtDateTime, fmtStat } from './format';

// =====================================================================
// Surveys UI composites (recharts-free — the Donut lives in charts.tsx so
// recharts stays out of the eager list bundle). Mirrors the site-analytics
// dashboard idiom: rounded-xl border-border-subtle bg-card panels, tabular-nums
// figures, sage chart tokens, no side-stripes.
// =====================================================================

export function SurveyStatusBadge({ status }: { status: SurveyStatus }) {
  const meta = SURVEY_STATUS_META[status];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export function Panel({
  title,
  children,
  className,
  headingLevel = 'h2',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  // Per-question panels pass 'h3' so they nest under the 'Per-question results'
  // <h2> section label instead of flattening the heading outline (WCAG 1.3.1).
  headingLevel?: 'h2' | 'h3';
}) {
  const Heading = headingLevel;
  return (
    <section
      className={cn(
        'flex flex-col gap-4 rounded-xl border border-border-subtle bg-card p-5',
        className,
      )}
    >
      <Heading className="text-sm font-semibold text-foreground">{title}</Heading>
      {children}
    </section>
  );
}

export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-card p-5">
      <p className="eyebrow">{label}</p>
      <p className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
      {hint ? <p className="text-xs text-text-tertiary">{hint}</p> : null}
    </div>
  );
}

// Labeled ranked bar — cleaner than a Recharts bar for long option labels. The
// fill is the subtle sunken track (same idiom as site-analytics), magnitude read
// by width; `suffix` carries the share (e.g. "· 42%").
export function BarRow({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  suffix?: string;
}) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <li className="relative overflow-hidden rounded-md px-3 py-2">
      <div className="relative z-10 flex items-baseline justify-between gap-3">
        <span className="truncate text-sm text-foreground">{label}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {value.toLocaleString()}
          {suffix ? <span className="ml-1 font-normal text-text-tertiary">{suffix}</span> : null}
        </span>
      </div>
      <div
        className="absolute inset-y-0 left-0 z-0 rounded-md"
        style={{ width: `${Math.max(pct, 2)}%`, background: CHART.barTrack }}
        aria-hidden="true"
      />
    </li>
  );
}

// A number / number_group field's min-mean-median-max.
export function NumericStatTile({ stat }: { stat: NumericStat }) {
  const cells: [string, number][] = [
    ['Min', stat.min],
    ['Mean', stat.mean],
    ['Median', stat.median],
    ['Max', stat.max],
  ];
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-sunken/60 p-4">
      {stat.label ? <p className="mb-2.5 text-sm font-medium text-foreground">{stat.label}</p> : null}
      <dl className="grid grid-cols-4 gap-2">
        {cells.map(([k, v]) => (
          <div key={k}>
            <dt className="eyebrow">{k}</dt>
            <dd className="text-base font-semibold tabular-nums text-foreground">{fmtStat(v)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2.5 text-xs text-text-tertiary">{stat.count.toLocaleString()} answered</p>
    </div>
  );
}

export function QualityBreakdown({ breakdown }: { breakdown: FlagBreakdown }) {
  const items: { key: string; label: string; count: number }[] = [
    { key: 'attention', label: 'Attention checks failed', count: breakdown.attentionFailed },
    { key: 'consistency', label: 'Inconsistent answers', count: breakdown.consistencyFailed },
    { key: 'straightlined', label: 'Straight-lined', count: breakdown.straightlined },
    { key: 'speeder', label: 'Completed too fast', count: breakdown.speeder },
  ];
  return (
    <>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
        {items.map((it) => (
          <div key={it.key} className="flex flex-col gap-1">
            <span
              className={cn(
                'text-2xl font-semibold tabular-nums',
                it.count > 0 ? 'text-danger-fg' : 'text-text-tertiary',
              )}
            >
              {it.count.toLocaleString()}
            </span>
            <span className="text-xs text-text-secondary">{it.label}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary">
        Quality checks flag responses worth a second look — they never remove anything.
      </p>
    </>
  );
}

export function WtpBody({ wtp }: { wtp: WtpSummary }) {
  const pts: { label: string; value: number | null; headline?: boolean }[] = [
    { label: 'Too cheap', value: wtp.tooCheap },
    { label: 'Bargain', value: wtp.cheap, headline: true },
    { label: 'Expensive', value: wtp.expensive },
    { label: 'Too expensive', value: wtp.tooExpensive },
  ];
  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {pts.map((p) => (
          <div key={p.label} className="flex flex-col gap-1">
            <span
              className={cn(
                'font-semibold tabular-nums',
                p.headline ? 'text-3xl text-brand-600' : 'text-xl text-foreground',
              )}
            >
              {p.value === null ? '—' : p.value.toLocaleString()}
            </span>
            <span className="text-xs text-text-secondary">
              {p.label}
              {p.headline ? ' (median)' : ''}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary">
        Bargain price = the sweet spot respondents consider good value. Based on{' '}
        {wtp.sampleSize.toLocaleString()} complete {wtp.sampleSize === 1 ? 'response' : 'responses'}.
      </p>
    </>
  );
}

// Public-link copy affordance for the 0-responses empty state.
export function ShareLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded-md bg-surface-sunken px-2.5 py-1.5 text-xs text-text-secondary">
        {url.replace(/^https?:\/\//, '')}
      </code>
      <Button type="button" variant="outline" size="sm" onClick={copy}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? 'Copied' : 'Copy link'}
      </Button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={buttonVariants({ variant: 'ghost', size: 'sm' })}
      >
        <ExternalLink className="h-4 w-4" />
        Open
      </a>
    </div>
  );
}

// Downloads the org-scoped responses CSV. Uses a raw fetch (not the JSON `api`
// helper) so we can stream the blob and trigger a file download; auth cookie
// rides along same-origin. `view` mirrors whatever filter is on screen — a CSV
// downloaded while the "released from hold" chip is active exports exactly
// that batch, not the blended default.
async function downloadResponsesCsv(
  businessId: string,
  surveyId: string,
  slug: string,
  view: SurveyResponseView,
): Promise<void> {
  // The Business id is passed in by the caller, which read it from the
  // canonical route. It is never derived from a Location id or from storage.
  const qs = view === 'released' ? '?view=released' : '';
  const res = await fetch(businessApi(businessId, `surveys/${surveyId}/responses.csv${qs}`), {
    credentials: 'same-origin',
    headers: { accept: 'text/csv' },
  });
  if (res.status === 401) {
    const here = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?redirect_after=${here}`;
    return;
  }
  if (!res.ok) {
    if (res.status === 403) throw new Error('CSV export needs the Survey add-on.');
    let code = `Download failed (${res.status}).`;
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object' && 'error' in body) {
        code = String((body as { error: unknown }).error);
      }
    } catch {
      /* non-JSON body — keep the status message */
    }
    throw new Error(code);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-responses.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// CSV export button. Teaser owners see a locked variant that routes to Billing
// (Option A: analytics visible to all, bulk export = the +$10 Survey add-on).
export function CsvButton({
  surveyId,
  slug,
  isTeaser,
  view = 'all',
}: {
  surveyId: string;
  slug: string;
  isTeaser: boolean;
  view?: SurveyResponseView;
}) {
  // The Business half of the canonical route this button is rendered under.
  const businessId = useBusinessId() ?? '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isTeaser) {
    return (
      <Link
        to={businessRoutes.billing(businessId)}
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
        aria-label="Download CSV — requires the Survey add-on"
        title="CSV export is part of the Survey add-on"
      >
        <Lock className="h-4 w-4" aria-hidden="true" />
        Download CSV
      </Link>
    );
  }

  async function onDownload() {
    setBusy(true);
    setError(null);
    try {
      await downloadResponsesCsv(businessId, surveyId, slug, view);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button type="button" variant="outline" size="sm" onClick={onDownload} disabled={busy}>
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="h-4 w-4" aria-hidden="true" />
        )}
        {busy ? 'Preparing…' : 'Download CSV'}
      </Button>
      {error ? (
        <span className="text-xs text-danger-fg" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

// "All / Released from hold (N)" — swaps the WHOLE analytics view (KPIs +
// table, via ?view= on the server) to just the previously-held batch. Blended
// ("All") is always the default; this chip only renders once N > 0, so there's
// never a filter with nothing to show.
export function ResponseViewToggle({
  view,
  releasedCount,
  onChange,
}: {
  view: SurveyResponseView;
  releasedCount: number;
  onChange: (next: SurveyResponseView) => void;
}) {
  if (releasedCount <= 0) return null;
  return (
    <SegmentedControl
      ariaLabel="Filter by response visibility"
      value={view}
      onChange={onChange}
      options={[
        { value: 'all', label: 'All' },
        { value: 'released', label: `Released from hold (${releasedCount})` },
      ]}
    />
  );
}

// Quiet notice for currently-held responses — mirrors the mailbox-panel /
// site-rank "role=status" informational idiom (not an alert: nothing is
// broken, the data is simply waiting on a response pack). Renders nothing
// when heldCount is 0.
//
// Copy is deliberately tier-neutral: "add a response pack" only reads true for
// an org that already has the survey add-on active on a paid plan (the pack
// route 403s everyone else — a free/teaser org needs the add-on first, or an
// upgrade before that). This component only receives `heldCount`, not the
// org's entitlement, and adding an API field just to gate this sentence isn't
// worth it — a neutral pointer to the billing page is honest for every tier.
export function HeldResponsesBanner({ heldCount }: { heldCount: number }) {
  const businessId = useBusinessId() ?? '';
  if (heldCount <= 0) return null;
  return (
    <p
      role="status"
      className="rounded-md border border-border-subtle bg-surface-sunken px-3 py-2 text-sm text-text-secondary"
    >
      {heldCount.toLocaleString()} {heldCount === 1 ? 'response is' : 'responses are'} held — see
      your options on the{' '}
      <Link to={businessRoutes.billing(businessId)} className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800">
        billing page
      </Link>
      .
    </p>
  );
}

function flagReason(r: SurveyResponseListItem): string {
  if (r.attentionPassed === false) return 'Attention';
  if (r.consistencyPassed === false) return 'Inconsistent';
  if (r.straightlined === true) return 'Straight-lined';
  if (r.speeder === true) return 'Too fast';
  return 'Flagged';
}

// Dense responses table (no Table primitive exists in the dashboard). Metadata
// only — individual answers live in the add-on-gated CSV. The "flagged only"
// filter is client-side over the already-fetched preview rows.
export function ResponsesTable({
  responses,
  total,
  flaggedCount,
}: {
  responses: SurveyResponseListItem[];
  total: number;
  flaggedCount: number;
}) {
  const [filter, setFilter] = useState<'all' | 'flagged'>('all');
  // The tab counts flagged rows IN the fetched preview — never the analytics-wide
  // total — so the label can't claim more than the filter can show.
  const flaggedInPreview = responses.filter((r) => r.flagged).length;
  const shown = filter === 'flagged' ? responses.filter((r) => r.flagged) : responses;
  const hasMore = total > responses.length;
  // Honest footer: when more flagged responses exist beyond the preview window,
  // say so; otherwise the usual "most recent of N" truncation note.
  const footerNote =
    filter === 'flagged' && flaggedCount > flaggedInPreview
      ? `Showing ${flaggedInPreview.toLocaleString()} flagged from the ${responses.length.toLocaleString()} most recent — ${flaggedCount.toLocaleString()} flagged in total. Download the CSV for all.`
      : hasMore
        ? `Showing the ${responses.length.toLocaleString()} most recent of ${total.toLocaleString()} responses. Download the CSV for the complete data.`
        : null;

  return (
    <section className="overflow-hidden rounded-xl border border-border-subtle bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-5 py-3.5">
        <h2 className="text-sm font-semibold text-foreground">Responses</h2>
        {flaggedInPreview > 0 ? (
          <SegmentedControl
            ariaLabel="Filter responses"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'flagged', label: `Flagged (${flaggedInPreview})` },
            ]}
          />
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {['Submitted', 'Segment', 'Quality', 'Source', 'Country'].map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="px-5 py-2.5 text-left eyebrow"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle border-t border-border-subtle">
            {shown.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-sm text-text-tertiary">
                  No flagged responses.
                </td>
              </tr>
            ) : (
              shown.map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-surface-sunken">
                  <td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-foreground">
                    {fmtDateTime(r.createdAt)}
                  </td>
                  <td className="px-5 py-2.5 text-text-secondary">{r.segment ?? '—'}</td>
                  <td className="px-5 py-2.5">
                    {r.flagged ? (
                      <Badge variant="destructive">{flagReason(r)}</Badge>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-text-secondary">{r.source ?? 'Direct'}</td>
                  <td className="px-5 py-2.5 text-text-secondary">{countryName(r.geoCountry)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {footerNote ? (
        <p className="border-t border-border-subtle px-5 py-3 text-xs text-text-tertiary">
          {footerNote}
        </p>
      ) : null}
    </section>
  );
}
